type HasPatientName = {
    PatientFirstName: string,
    PatientLastName: string,
    PatientPreferredName: string
};

type HasName = {
    FirstName: string,
    LastName: string,
    PreferredName?: string,

    PrefixLookupId?: string,
    Prefix?: string,
};
// changes here should be duplicated in the back-end helper: Myle.Core/Myle.Core/Helpers/NameHelper.cs
class NameHelper {
    /**
     * Format a patient's full name for an entity that has patient name properties.
     * @param hasPatientName The entity that has patient name properties
     * @param format The name format.
     * @error The format is out of range.
     */
    public static formatFullNameForHasPatientName(hasPatientName: HasPatientName, format: NameHelperFormat = NameHelperFormat.LastNameFirst): string {
        return NameHelper.formatFullName(
            hasPatientName.PatientFirstName,
            hasPatientName.PatientLastName,
            hasPatientName.PatientPreferredName,
            format
        );
    }

    /**
     * Format a full name for an entity that has name properties.
     * @param hasName The entity that has name properties
     * @param format The name format.
     * @error The format is out of range.
     */
    public static formatFullNameForHasName(hasName: HasName, format: NameHelperFormat = NameHelperFormat.LastNameFirst): string {
        return NameHelper.formatFullName(
            hasName.FirstName,
            hasName.LastName,
            hasName.PreferredName,
            format
        );
    }

    /**
     * Format a full name for an entity that has name properties.
     * @param hasName The entity that has name properties
     * @param format The name format.
     * @error The format is out of range.
     */
    public static formatFullNameWithPrefixForHasName(hasName: HasName, format: NameHelperFormat = NameHelperFormat.LastNameFirst): string {
        if (!IsNullOrWhiteSpace(hasName.Prefix)) {
            return NameHelper.formatFullNameWithPrefix(
                hasName.FirstName,
                hasName.LastName,
                hasName.PreferredName,
                hasName.Prefix,
                format
            );
        }

        return NameHelper.formatFullNameWithPrefixId(
            hasName.FirstName,
            hasName.LastName,
            hasName.PreferredName,
            hasName.PrefixLookupId,
            format
        );
    }

    /**
     * Format a full name for the given values.
     * @param firstName The first name.
     * @param lastName The last name.
     * @param preferredName The preferred name, if any.
     * @param format The name format.
     * @error The format is out of range.
     */
    public static formatFullName(firstName: string, lastName: string, preferredName: string, format: NameHelperFormat = NameHelperFormat.LastNameFirst): string {
        const firstAndPreferredName = NameHelper.formatFirstAndPreferredName(firstName, preferredName);

        if (IsNullOrWhiteSpace(lastName))
            return firstAndPreferredName;

        switch (format) {
            case NameHelperFormat.LastNameFirst:
                return `${lastName.trim()}, ${firstAndPreferredName}`;

            case NameHelperFormat.UpperLastNameFirst:
                return `${lastName.trim().toUpperCase()}, ${firstAndPreferredName}`;

            case NameHelperFormat.FirstNameFirst:
                return `${firstAndPreferredName} ${lastName.trim()}`;

            default:
                throw new Error("Format must be either 'LastNameFirst' or 'FirstNameFirst'.");
        }
    }

    /**
     * Format a full name for the given values.
     * @param firstName The first name.
     * @param lastName The last name.
     * @param preferredName The preferred name.
     * @param prefix The prefix
     * @param format The name format.
     * @error The format is out of range.
     */
    public static formatFullNameWithPrefix(firstName: string, lastName: string, preferredName: string, prefix: string, format: NameHelperFormat = NameHelperFormat.LastNameFirst): string {
        const fullName = NameHelper.formatFullName(firstName, lastName, preferredName, format);

        if (IsNullOrWhiteSpace(fullName))
            return "";

        if (IsNullOrWhiteSpace(prefix))
            return fullName;

        switch (format) {
            // format the prefix at the end for last-name-first to preserve alphabetic sorting
            case NameHelperFormat.LastNameFirst:
            case NameHelperFormat.UpperLastNameFirst:
                return `${fullName}, ${prefix}`;

            case NameHelperFormat.FirstNameFirst:
                return `${prefix} ${fullName}`;

            default:
                throw new Error("Format must be either 'LastNameFirst' or 'FirstNameFirst'.");
        }
    }

    /**
     * Format a full name for the given values.
     * @param firstName The first name.
     * @param lastName The last name.
     * @param preferredName The preferred name.
     * @param prefixLookupId The prefix lookup ID.
     * @param format The name format.
     * @error The format is out of range.
     */
    public static formatFullNameWithPrefixId(firstName: string, lastName: string, preferredName: string, prefixLookupId: string, format: NameHelperFormat = NameHelperFormat.LastNameFirst): string {
        return NameHelper.formatFullNameWithPrefix(
            firstName,
            lastName,
            preferredName,
            cacheStore.GetLocalizedValueByLookupId(prefixLookupId),
            format
        );
    }

    /**
     * Format the given first and preferred names.
     * @param firstName The first name.
     * @param preferredName The preferred name, if any.
     */
    private static formatFirstAndPreferredName(firstName: string, preferredName?: string): string {
        if (!IsNullOrWhiteSpace(firstName) && !IsNullOrWhiteSpace(preferredName))
            return `${firstName.trim()} (${preferredName.trim()})`;

        if (!IsNullOrWhiteSpace(preferredName))
            return preferredName.trim();

        return firstName?.trim() ?? "";
    }
}

enum NameHelperFormat {
    /**
     * The name is formatted with the last name first.
     * e.g. Last, First (Preferred), Prefix.
     */
    LastNameFirst,

    /**
     * The name is formatted with the uppercase last name first.
     * e.g. LAST, First (Preferred), Prefix.
     */
    UpperLastNameFirst,

    /**
     * The name is formatted with the first name first.
     * e.g. Prefix. First (Preferred) Last
     */
    FirstNameFirst
}

export { NameHelper, NameHelperFormat };
